<div class="d-flex flex-wrap flex-stack mb-6">
  <h3 class="fw-bold my-2">{{ 'manageEvent.tabs.coOrganisers' | translate }}</h3>
  <div class="d-flex flex-wrap my-2 gap-2">
    <app-button
      [variant]="'outline'"
      [label]="'manageEvent.members.addMember' | translate"
      [iconClass]="'fa fa-user-plus'"
      (clicked)="openAddMemberModal()"
    />
    <app-button
      [variant]="'primary'"
      [label]="'manageEvent.coOrganisers.addCoOrganiser' | translate"
      [iconClass]="'fa fa-plus'"
      (clicked)="openAddModal()"
    />
  </div>
</div>

<div class="alert alert-info d-flex align-items-center mb-6">
  <i class="fa fa-info-circle fs-4 me-3"></i>
  <div>{{ 'manageEvent.coOrganisers.infoText' | translate }}</div>
</div>

<!-- Companies Section -->
<div class="mb-8">
  <h5 class="fw-semibold text-gray-700 mb-4">
    <i class="fa fa-building me-2 text-muted"></i>{{ 'manageEvent.members.companiesSection' | translate }}
  </h5>

  <div class="row g-6 g-xl-9" [class.opacity-50]="loading" [class.pointer-events-none]="loading">
    @for (coOrganiser of coOrganisers; track coOrganiser.entry_id) {
      <div class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div class="flex-grow-1">
                <div class="d-flex align-items-center mb-2">
                  @if (coOrganiser.company?.company_logo_url) {
                    <img [src]="coOrganiser.company?.company_logo_url" class="rounded-circle me-3" width="40" height="40" alt="">
                  } @else {
                    <div class="symbol symbol-40px me-3">
                      <span class="symbol-label bg-light-primary text-primary fw-bold">
                        {{ coOrganiser.company?.company_name?.charAt(0) || '?' }}
                      </span>
                    </div>
                  }
                  <div>
                    <h4 class="mb-0">{{ coOrganiser.company?.company_name || ('common.unknown' | translate) }}</h4>
                  </div>
                </div>
              </div>
              <div class="dropdown">
                <button class="btn btn-sm btn-light btn-icon d-flex align-items-center justify-content-center" type="button" data-bs-toggle="dropdown">
                  <i class="fa fa-ellipsis-v"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li>
                    <a class="dropdown-item text-danger" (click)="removeCoOrganiser(coOrganiser)">
                      <i class="fa fa-trash me-2"></i>{{ 'common.remove' | translate }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="separator mb-3"></div>

            <div class="mb-3">
              <label class="form-label fw-semibold">{{ 'manageEvent.coOrganisers.accessLevel' | translate }}</label>
              <div class="d-flex flex-wrap gap-2">
                @for (option of accessLevelOptions; track option.value) {
                  <button
                    type="button"
                    class="btn btn-sm"
                    [class.btn-primary]="coOrganiser.access_level === option.value"
                    [class.btn-light]="coOrganiser.access_level !== option.value"
                    [title]="option.description | translate"
                    (click)="updateAccessLevel(coOrganiser, option.value)"
                  >
                    {{ option.label | translate }}
                  </button>
                }
              </div>
            </div>

            @if (coOrganiser.company?.email || coOrganiser.company?.phone) {
              <div class="d-flex flex-column gap-2 mt-auto">
                @if (coOrganiser.company?.email) {
                  <div class="d-flex align-items-center">
                    <i class="fa fa-envelope text-muted me-2"></i>
                    <span class="text-gray-700">{{ coOrganiser.company?.email }}</span>
                  </div>
                }
                @if (coOrganiser.company?.phone) {
                  <div class="d-flex align-items-center">
                    <i class="fa fa-phone text-muted me-2"></i>
                    <span class="text-gray-700">{{ coOrganiser.company?.phone }}</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
    @if (!loading && (!coOrganisers || coOrganisers.length === 0)) {
      <div class="col-12 text-center text-muted py-6">{{ 'manageEvent.coOrganisers.noCoOrganisers' | translate }}</div>
    }
  </div>
</div>

<!-- Members Section -->
<div>
  <h5 class="fw-semibold text-gray-700 mb-4">
    <i class="fa fa-users me-2 text-muted"></i>{{ 'manageEvent.members.membersSection' | translate }}
  </h5>

  <div class="row g-6 g-xl-9" [class.opacity-50]="loadingMembers" [class.pointer-events-none]="loadingMembers">
    @for (member of members; track member.member_id) {
      <div class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div class="flex-grow-1">
                <div class="d-flex align-items-center mb-2">
                  <div class="symbol symbol-40px me-3">
                    <span class="symbol-label bg-light-info text-info fw-bold">
                      {{ getMemberInitials(member) }}
                    </span>
                  </div>
                  <div>
                    <h4 class="mb-0">{{ getMemberFullName(member) }}</h4>
                    @if (member.user?.email) {
                      <small class="text-muted">{{ member.user?.email }}</small>
                    }
                  </div>
                </div>
              </div>
              <div class="dropdown">
                <button class="btn btn-sm btn-light btn-icon d-flex align-items-center justify-content-center" type="button" data-bs-toggle="dropdown">
                  <i class="fa fa-ellipsis-v"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li>
                    <a class="dropdown-item text-danger" (click)="removeMember(member)">
                      <i class="fa fa-trash me-2"></i>{{ 'common.remove' | translate }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="separator mb-3"></div>

            <div class="mb-3">
              <label class="form-label fw-semibold">{{ 'manageEvent.coOrganisers.accessLevel' | translate }}</label>
              <div class="d-flex flex-wrap gap-2">
                @for (option of accessLevelOptions; track option.value) {
                  <button
                    type="button"
                    class="btn btn-sm"
                    [class.btn-primary]="member.access_level === option.value"
                    [class.btn-light]="member.access_level !== option.value"
                    [title]="option.description | translate"
                    (click)="updateMemberAccessLevel(member, option.value)"
                  >
                    {{ option.label | translate }}
                  </button>
                }
              </div>
            </div>

            @if (member.user?.email || member.user?.phone) {
              <div class="d-flex flex-column gap-2 mt-auto">
                @if (member.user?.email) {
                  <div class="d-flex align-items-center">
                    <i class="fa fa-envelope text-muted me-2"></i>
                    <span class="text-gray-700">{{ member.user?.email }}</span>
                  </div>
                }
                @if (member.user?.phone) {
                  <div class="d-flex align-items-center">
                    <i class="fa fa-phone text-muted me-2"></i>
                    <span class="text-gray-700">{{ member.user?.phone }}</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }
    @if (!loadingMembers && (!members || members.length === 0)) {
      <div class="col-12 text-center text-muted py-6">{{ 'manageEvent.members.noMembers' | translate }}</div>
    }
  </div>
</div>
